import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Outlet, RouteObject } from 'react-router';

import { LoadingScreen } from '@zeta-core';
import { RouterLinks } from '../../app-route-paths';
import { AuthGuard } from '../../auth/auth-guard';
import ProtectedRoute from '../../auth/protected-route';
import { CompactErrorPage } from '../../layouts/compact-layout';
import { DashboardLayout } from '../../layouts/dashboard-layout';
import DashboardContainer from '../../layouts/dashboard-layout/dashboard-container';

const WelcomePage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.WelcomePage })));
const GoalsPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.GoalsPage })));
const PersonalFamilyFactFindPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.PersonalFamilyFactFindPage })));
const IncomeFactFindPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.IncomeFactFindPage })));
const AssetsFactFindPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.AssetsFactFindPage })));
const LiabilitiesFactFindPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.LiabilitiesFactFindPage })));
const EntitiesFactFindPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.EntitiesFactFindPage })));
const ProtectionFactFindPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.ProtectionFactFindPage })));
const SpendingFactFindPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.SpendingFactFindPage })));
const RiskProfilePage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.RiskProfilePage })));
const ScheduleMeetingPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.ScheduleMeetingPage })));
const MeetingDetailsPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.MeetingDetailsPage })));
const MeetingSummaryPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.MeetingSummaryPage })));
const ClientDocumentsPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.ClientDocumentsPage })));
const Client360Page = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.Client360Page })));
const ClientPlansPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.ClientPlansPage })));
const ClientPlanScenarioPage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.ClientPlanScenarioPage })));
const ClientPutInPlacePage = lazy(() => import('../../client-advice/pages').then((module) => ({ default: module.ClientPutInPlacePage })));
const ChangePasswordPage = lazy(() => import('../../pages/auth/change-password'));

const DashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: (
      <ErrorBoundary FallbackComponent={CompactErrorPage}>
        <Suspense fallback={<LoadingScreen />}>
          <AuthGuard>
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          </AuthGuard>
        </Suspense>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={RouterLinks.workspace} replace />,
      },
      {
        path: 'workspace',
        element: <WelcomePage />,
      },
      {
        path: 'build-your-picture',
        element: <Client360Page />,
      },
      {
        path: 'goals',
        element: <GoalsPage />,
      },
      {
        path: 'fact-find/personal-family',
        element: <PersonalFamilyFactFindPage />,
      },
      {
        path: 'fact-find/income',
        element: <IncomeFactFindPage />,
      },
      {
        path: 'fact-find/assets',
        element: <AssetsFactFindPage />,
      },
      {
        path: 'fact-find/liabilities',
        element: <LiabilitiesFactFindPage />,
      },
      {
        path: 'fact-find/entities',
        element: <EntitiesFactFindPage />,
      },
      {
        path: 'fact-find/protection',
        element: <ProtectionFactFindPage />,
      },
      {
        path: 'fact-find/spending',
        element: <SpendingFactFindPage />,
      },
      {
        path: 'risk-profile',
        element: <RiskProfilePage />,
      },
      {
        path: 'schedule-meeting',
        element: <ScheduleMeetingPage />,
      },
      {
        path: 'meetingdetail',
        element: <MeetingDetailsPage />,
      },
      {
        path: 'prepare',
        element: <ScheduleMeetingPage />,
      },
      {
        path: 'discovery-summary',
        element: <MeetingSummaryPage />,
      },
      {
        path: 'documents',
        element: <ClientDocumentsPage />,
      },
      {
        path: 'client-360',
        element: <Client360Page />,
      },
      {
        path: 'plans',
        element: <ClientPlansPage />,
      },
      {
        path: 'plans/:scenarioId',
        element: <ClientPlanScenarioPage />,
      },
      {
        path: 'put-in-place',
        element: <ClientPutInPlacePage />,
      },
      {
        path: '*',
        element: <WelcomePage />,
      },
    ],
  },
  {
    path: 'profile-setup',
    element: (
      <ErrorBoundary FallbackComponent={CompactErrorPage}>
        <Suspense fallback={<LoadingScreen />}>
          <AuthGuard>
            <ProtectedRoute>
              <DashboardLayout>
                <Outlet />
              </DashboardLayout>
            </ProtectedRoute>
          </AuthGuard>
        </Suspense>
      </ErrorBoundary>
    ),
    children: [
      {
        path: 'change-password',
        element: (
          <DashboardContainer variant="sm">
            <ChangePasswordPage />
          </DashboardContainer>
        ),
      },
      {
        path: '*',
        element: <Navigate to={RouterLinks.changePassword} replace />,
      },
    ],
  },
];

export default DashboardRoutes;
